/* Unpublished PDP purchase connector. Subscription retains Kaching -> /cart;
   one-time creates a server-priced checkout without touching another cart. */
(() => {
  'use strict';
  const ENGINE = 'https://checkout.holtmore.com';
  const TRACK_KEYS = ['fbclid', 'gclid', 'ttclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
  const CART_ADD_TIMEOUT_MS = 15000;
  const CREATE_TIMEOUT_MS = 30000;
  const CART_READ_TIMEOUT_MS = 5000;
  const CART_REVIEW_MESSAGE = 'We could not confirm your cart. Please open your cart and check the items before trying again.';
  const pause = ms => new Promise(resolve => setTimeout(resolve, ms));
  const unavailable = () => new Error('Please try again in a moment.');
  const invalidSelection = () => new Error('Please select your bundle again.');

  function captureTracking() {
    let tracking = {};
    try {
      const saved = JSON.parse(localStorage.getItem('__ce_track') || '{}');
      if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
        tracking = Object.fromEntries(Object.entries(saved).filter(([, value]) => typeof value === 'string'));
      }
    } catch (_) { /* Attribution must not prevent checkout. */ }
    const params = new URLSearchParams(location.search);
    TRACK_KEYS.forEach(key => {
      const value = params.get(key);
      if (value && !tracking[key]) tracking[key] = value;
    });
    if (!tracking.landing_url) tracking.landing_url = location.href;
    for (const [key, cookie] of [['fbp', '_fbp'], ['fbc', '_fbc']]) {
      try {
        const match = document.cookie.match(new RegExp('(?:^|; )' + cookie + '=([^;]*)'));
        if (match) tracking[key] = decodeURIComponent(match[1]);
      } catch (_) { /* A blocked cookie cannot prevent checkout. */ }
    }
    try { localStorage.setItem('__ce_track', JSON.stringify(tracking)); } catch (_) {}
    return tracking;
  }

  function init(scope) {
    const root = scope.matches?.('[data-hmc]') ? scope : scope.querySelector?.('[data-hmc]');
    if (!root) return;
    const form = root.querySelector('#hm-live-bundle-form') || document.getElementById('hm-live-bundle-form');
    const box = root.querySelector('#hmc-s9');
    const button = root.querySelector('#hmcAtc');
    if (!form || !box || !button || button.dataset.hmLiveCartReady) return;
    const cartUrl = new URL('/cart', location.origin);
    const preview = new URLSearchParams(location.search).get('preview_theme_id') ||
      (window.Shopify?.theme?.role !== 'main' ? document.body.dataset.hmTheme : null);
    if (preview) cartUrl.searchParams.set('preview_theme_id', preview);
    let busy = false;
    let navigating = false;
    let cartOutcomeUnknown = false;
    let restoreControls = () => {};

    function report(error) {
      console.error('[holtmore-cart]', error);
      const toast = root.querySelector('[data-hmc-toast]');
      if (!toast) return;
      toast.textContent = error.message || 'Please try again in a moment.';
      toast.classList.add('is-show');
      if (!cartOutcomeUnknown) setTimeout(() => toast.classList.remove('is-show'), 5000);
    }

    async function fetchJsonWithin(url, options, timeoutMs) {
      const controller = new AbortController();
      let timer;
      try {
        return await Promise.race([
          (async () => {
            const response = await fetch(url, { ...options, signal: controller.signal });
            return { response, data: await response.json().catch(() => null) };
          })(),
          new Promise((_, reject) => {
            timer = setTimeout(() => {
              reject(unavailable());
              controller.abort();
            }, timeoutMs);
          }),
        ]);
      } finally {
        clearTimeout(timer);
      }
    }

    async function inspectUnknownCart(items) {
      // A timed-out/failed POST might still reach Shopify. Never send it again
      // from this page, even if a readback appears empty or fails.
      cartOutcomeUnknown = true;
      try {
        const { response, data } = await fetchJsonWithin('/cart.js', {
          method: 'GET', credentials: 'same-origin', cache: 'no-store',
        }, CART_READ_TIMEOUT_MS);
        if (response.ok && Array.isArray(data?.items)) {
          const required = new Map();
          const actual = new Map();
          items.forEach(item => required.set(String(item.id), (required.get(String(item.id)) || 0) + item.quantity));
          data.items.forEach(item => actual.set(String(item.variant_id ?? item.id),
            (actual.get(String(item.variant_id ?? item.id)) || 0) + item.quantity));
          if ([...required].every(([id, quantity]) => (actual.get(id) || 0) >= quantity)) {
            navigating = true;
            location.href = cartUrl.pathname + cartUrl.search;
            return;
          }
        }
      } catch (_) { /* Unknown cart outcome stays blocked for manual review. */ }
      throw new Error(CART_REVIEW_MESSAGE);
    }

    function cents(value) {
      if (!/^\d+\.\d{2}$/.test(value || '')) return null;
      const amount = Math.round(Number(value) * 100);
      return Number.isSafeInteger(amount) && amount > 0 ? amount : null;
    }

    function selectedPurchase() {
      if (box.dataset.buyboxReady !== 'true') throw invalidSelection();
      const cards = Array.from(box.querySelectorAll('.hmc-offer'));
      const checked = cards.filter(card => card.querySelector('input[name="hmc-offer"]')?.checked);
      const marked = cards.filter(card => card.classList.contains('is-sel'));
      if (cards.length !== 3 || checked.length !== 1 || marked.length !== 1 || checked[0] !== marked[0]) throw invalidSelection();
      const card = checked[0];
      if (cards.filter(item => item.dataset.selected === 'true').length !== 1 || card.dataset.selected !== 'true') throw invalidSelection();
      const mode = card.dataset.mode;
      const quantity = Number(card.dataset.bottles);
      const currency = card.dataset.currency;
      const priceCents = Number(card.dataset.priceCents);
      const price = card.dataset.price;
      const input = card.querySelector('input[name="hmc-offer"]');
      if (!['subscription', 'onetime'].includes(mode) || ![1, 2, 4].includes(quantity) ||
          !['USD', 'GBP', 'EUR', 'AUD', 'CAD', 'NZD'].includes(currency) ||
          !/^\d+$/.test(card.dataset.priceCents || '') || !Number.isSafeInteger(priceCents) || priceCents <= 0 ||
          card.dataset.offer !== String(quantity) || input?.value !== String(quantity) ||
          cents(price) !== priceCents ||
          cents(mode === 'onetime' ? card.dataset.onetimePrice : card.dataset.subscriptionPrice) !== priceCents ||
          box.dataset.purchaseMode !== mode || button.dataset.mode !== mode ||
          box.dataset.bottles !== String(quantity) || button.dataset.bottles !== String(quantity) ||
          box.dataset.currency !== currency || button.dataset.currency !== currency ||
          box.dataset.priceCents !== String(priceCents) || button.dataset.priceCents !== String(priceCents) ||
          box.dataset.price !== price || button.dataset.price !== price ||
          (window.Shopify?.currency?.active && window.Shopify.currency.active !== currency)) {
        throw invalidSelection();
      }
      return { card, mode, quantity, currency, priceCents };
    }

    function stillSelected(expected) {
      const current = selectedPurchase();
      if (current.card !== expected.card || current.mode !== expected.mode ||
          current.quantity !== expected.quantity || current.currency !== expected.currency ||
          current.priceCents !== expected.priceCents) throw invalidSelection();
    }

    async function settled(read) {
      const until = Date.now() + 5000;
      while (Date.now() < until) {
        const value = read();
        if (value) return value;
        await pause(50);
      }
      throw unavailable();
    }

    async function nativeBundle() {
      const until = Date.now() + 12000;
      while (Date.now() < until) {
        const block = form.querySelector('kaching-bundles-block');
        if (block && typeof block.items === 'function' && block.dealBlock?.dealBars) return block;
        await pause(100);
      }
      throw unavailable();
    }

    async function addSubscription(selection) {
      const block = await nativeBundle();
      const offers = block.dealBlock.dealBars;
      const candidates = offers.filter(offer => {
        const gifts = (offer.upsells || []).filter(gift => gift.discountType === 'percentage' && gift.discountValue === 100 && /bottle/i.test(gift.text || ''));
        return selection.quantity === 1 ? offer.quantity === 1 && gifts.length === 0
          : selection.quantity === 2 ? offer.quantity === 1 && gifts.some(gift => gift.quantity === 1)
          : offer.quantity === 2 && gifts.some(gift => gift.quantity === 2);
      });
      if (candidates.length !== 1) throw unavailable();
      const offer = candidates[0];
      block.selectedDealBarId = offer.id;
      const expectedGifts = (offer.upsells || []).filter(gift => gift.discountType === 'percentage' && gift.discountValue === 100);
      const checks = await settled(() => {
        const selectedBar = block.querySelector('.kaching-bundles__bar--selected[data-deal-bar-id]');
        if (selectedBar?.getAttribute('data-deal-bar-id') !== offer.id) return false;
        const nodes = Array.from(selectedBar.querySelectorAll('.kaching-bundles__upsell__checkbox'));
        return nodes.length === expectedGifts.length ? nodes : false;
      });
      checks.forEach(check => { if (!check.classList.contains('kaching-bundles__upsell__checkbox--selected')) check.click(); });
      const mainId = Number(form.dataset.variantId);
      if (!Number.isSafeInteger(mainId) || mainId <= 0) throw unavailable();
      const lines = await settled(() => {
        if (!block.isItemSelectionValid() || Math.round(block.pricing().discountedPrice.amount * 100) !== selection.priceCents) return false;
        const raw = block.items();
        const current = raw?.items || raw;
        if (!Array.isArray(current) || !current.length) return false;
        const mainQty = current.filter(line => Number(line.id) === mainId).reduce((sum, line) => sum + line.quantity, 0);
        return mainQty === (selection.quantity === 4 ? 2 : 1) &&
          current.reduce((sum, line) => sum + line.quantity, 0) === selection.quantity + (selection.quantity === 4 ? 1 : 0) ? current : false;
      });
      stillSelected(selection);
      const items = lines.map(line => ({ ...line, properties: { ...line.properties, ...(Number(line.id) === mainId ? { _ce_sub_days: '28' } : {}) } }));
      let response, result;
      try {
        const outcome = await fetchJsonWithin('/cart/add.js?kaching_bundles=true', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        }, CART_ADD_TIMEOUT_MS);
        response = outcome.response;
        result = outcome.data || {};
      } catch (_) {
        await inspectUnknownCart(items);
        return;
      }
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Please try again in a moment.');
      stillSelected(selection);
      navigating = true;
      location.href = cartUrl.pathname + cartUrl.search;
    }

    async function createOneTime(selection) {
      const variantId = Number(form.dataset.variantId);
      const productId = Number(form.dataset.productId);
      const title = (form.dataset.productTitle || '').trim();
      const shop = form.dataset.shop;
      if (!Number.isSafeInteger(variantId) || variantId <= 0 || !title || !shop) throw unavailable();
      const item = {
        variantId, title, quantity: selection.quantity,
        priceCents: Math.floor(selection.priceCents / selection.quantity),
        linePriceCents: selection.priceCents,
        sellingPlanDays: null,
        ...(Number.isSafeInteger(productId) && productId > 0 ? { productId } : {}),
        ...(form.dataset.variantTitle ? { variantTitle: form.dataset.variantTitle } : {}),
        ...(form.dataset.image ? { image: form.dataset.image } : {}),
      };
      stillSelected(selection);
      const { response, data } = await fetchJsonWithin(ENGINE + '/api/checkout/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop, cartToken: null, currency: selection.currency,
          totalCents: selection.priceCents, items: [item],
          tracking: captureTracking(),
          purchaseMode: 'one_time', oneTimeQuantity: selection.quantity,
        })
      }, CREATE_TIMEOUT_MS);
      if (!response.ok || data?.purchaseMode !== 'one_time' ||
          data.oneTimeQuantity !== selection.quantity ||
          data.currency !== selection.currency || data.subtotalCents !== selection.priceCents ||
          typeof data.checkoutUrl !== 'string') throw unavailable();
      let checkoutUrl;
      try {
        checkoutUrl = new URL(data.checkoutUrl);
        if (checkoutUrl.origin !== ENGINE || !/^\/c\/[^/]+\/?$/.test(checkoutUrl.pathname)) throw unavailable();
      } catch (_) { throw unavailable(); }
      stillSelected(selection);
      navigating = true;
      location.href = checkoutUrl.href;
    }

    function lockControls() {
      const controls = [button, box.querySelector('[data-purchase-toggle]'), ...box.querySelectorAll('input[name="hmc-offer"]')].filter(Boolean);
      const previous = controls.map(control => [control, control.disabled]);
      controls.forEach(control => { control.disabled = true; });
      restoreControls = () => previous.forEach(([control, disabled]) => { control.disabled = disabled; });
      button.setAttribute('aria-busy', 'true');
    }

    function release() {
      busy = false;
      navigating = false;
      restoreControls();
      button.removeAttribute('aria-busy');
    }

    async function add() {
      if (busy) return;
      busy = true;
      lockControls();
      try {
        const selection = selectedPurchase();
        if (selection.mode === 'subscription') await addSubscription(selection);
        else await createOneTime(selection);
      } catch (error) { report(error); }
      finally {
        if (!cartOutcomeUnknown) {
          if (navigating) setTimeout(release, 8000);
          else release();
        }
      }
    }

    root.addEventListener('click', event => {
      if (busy && event.target.closest?.('[data-buy]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
    window.addEventListener?.('pageshow', event => {
      if (event.persisted && cartOutcomeUnknown) report(new Error(CART_REVIEW_MESSAGE));
      else if (event.persisted) release();
    });
    button.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); add(); });
    form.addEventListener('submit', event => { event.preventDefault(); add(); });
    button.dataset.hmLiveCartReady = 'true';
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
